import { loadMessages } from '@progress/kendo-react-intl';
import { TFunction } from 'i18next';
import { getHostAndDomain } from '../../../../common/global/helpers';
import { config } from '../../config';
import { resources } from '../../resources/resources';
import { ICustomerInfoContext } from '../../contexts/common';

export const getCspMeta = (): string => {
  const defaultSrcs = [`'self'`];
  const connectSrcs = [
    `'self'`,
    getHostAndDomain(config.benefitApiUrl!),
    getHostAndDomain(config.idpUrl!),
    'https://druidapi.druidplatform.com',
    'https://directline.botframework.com',
    'https://privacyportal-de.onetrust.com',
    'https://cdn.cookielaw.org',
    'https://qkbjprs.pa-cd.com',
    'https://*.in.applicationinsights.azure.com',
  ];
  const scriptSrcs = [
    `'self'`,
    'https://prod-druid-apc.azureedge.net',
    'https://cdn.cookielaw.org',
    `'sha256-5VrVgGfPbUH5IoPb+tGodpswZad/XDHQfqHeVD0LMG4='`, //hash for inline OneTrust script: function OptanonWrapper() {}
    'https://tag.aticdn.net',
  ];
  const objectSrcs = ['https://www.benefitsystems.ro'];
  const styleSrcs = [
    `'self'`,
    `'unsafe-inline'`,
    'https://fonts.googleapis.com',
    'https://prod-druid-apc.azureedge.net',
  ];
  const fontSrcs = [`'self'`, 'data: https://fonts.gstatic.com'];
  const imgSrcs = [
    `'self'`,
    `data: ${getHostAndDomain(config.imagesUrl)}`,
    `data: ${getHostAndDomain(config.videosUrl)}`, //for video thumbnails
    'https://www.edenred.ro',
    'https://www.benefitsystems.ro',
    'https://cdn.cookielaw.org',
  ];
  const mediaSrcs = [`'self'`, `data: ${getHostAndDomain(config.videosUrl!)}`];
  const frameSrcs = [
    `'self'`,
    'https://privacyportal-de.onetrust.com',
    'https://www.benefitsystems.ro',
    getHostAndDomain(config.idpUrl),
  ];

  return `
    default-src ${defaultSrcs.join(' ')};
    connect-src ${connectSrcs.join(' ')};
    script-src ${scriptSrcs.join(' ')};
    object-src ${objectSrcs.join(' ')};
    style-src ${styleSrcs.join(' ')};
    font-src ${fontSrcs.join(' ')};
    img-src ${imgSrcs.join(' ')};
    media-src ${mediaSrcs.join(' ')};
    frame-src ${frameSrcs.join(' ')};
  `;
};

export const loadKendoCustomMessages = (
  translate: TFunction,
  customerInfo: ICustomerInfoContext,
) => {
  const currentLanguage = customerInfo?.languageCode ?? config.defaultLanguage;

  loadMessages(
    {
      upload: {
        select: translate(resources.upload.button.text),
        dropFilesHere: translate(resources.upload.dropZone.hint),
        headerStatusUploading: translate(
          resources.upload.status.header.uploading,
        ),
        headerStatusUploaded: translate(
          resources.upload.status.header.uploaded,
        ),
        statusUploaded: translate(resources.upload.status.success),
        statusUploadFailed: translate(resources.upload.status.failed),
      },
      calendar: {
        today: translate(resources.calendar.today),
      },
      timepicker: {
        cancel: translate(resources.timepicker.cancel),
        set: translate(resources.timepicker.set),
        now: translate(resources.timepicker.now),
      },
      grid: {
        noRecords: translate(resources.grid.noRecords),
      },
    },
    currentLanguage ?? 'ro',
  );
};

declare global {
  // eslint-disable-next-line no-var
  var __ONE_TRUST_INJECTED__: boolean;
}

export const injectOneTrustScripts = (isOneTrustActive: boolean): void => {
  const SCRIPT_ID = 'onetrust-main-script';

  if (
    !isOneTrustActive ||
    globalThis.__ONE_TRUST_INJECTED__ ||
    document.getElementById(SCRIPT_ID) ||
    process.env.NODE_ENV === 'development'
  ) {
    return;
  }

  globalThis.__ONE_TRUST_INJECTED__ = true;

  const autoBlockScript = document.createElement('script');
  autoBlockScript.src = `https://cdn.cookielaw.org/consent/${config.onetrustCookiesDataDomainId}/OtAutoBlock.js`;
  autoBlockScript.type = 'text/javascript';
  autoBlockScript.async = false;

  const inlineScript = document.createElement('script');
  inlineScript.type = 'text/javascript';
  inlineScript.textContent = 'function OptanonWrapper() {}';

  const mainScript = document.createElement('script');
  mainScript.id = SCRIPT_ID;
  mainScript.src = config.onetrustCookiesScriptSrc;
  mainScript.type = 'text/javascript';
  mainScript.dataset.domainScript = config.onetrustCookiesDataDomainId;
  mainScript.async = true;

  document.head.prepend(mainScript);
  document.head.prepend(inlineScript);
  document.head.prepend(autoBlockScript);
};
